// ══════════════════════════════════════════════
//  fecha.logic — motor puro del selector de fecha (DateInput), 2026-09-10.
//  Máscara dd/mm/aaaa tolerante, lectura de lo tecleado o pegado y
//  cuadrícula del mes. Sin DOM. El valor canónico de la app es 'YYYY-MM-DD'
//  (ver `today` en utils.js); aquí no se usa Date local para no resbalar
//  por zona horaria: todo es aritmética UTC sobre el ISO.
// ══════════════════════════════════════════════
import { addDias } from '../../calendario.logic.js';

export const MESES = [
  'enero', 'febrero', 'marzo', 'abril', 'mayo', 'junio',
  'julio', 'agosto', 'septiembre', 'octubre', 'noviembre', 'diciembre',
];
// La semana se lee de lunes a domingo, como el resto de la app (calendario.logic)
export const DIAS_CORTOS = ['L', 'M', 'X', 'J', 'V', 'S', 'D'];

const pad2 = (n) => String(n).padStart(2, '0');
const diasEnMes = (anio, mes1) => new Date(Date.UTC(anio, mes1, 0)).getUTCDate();

export const esISO = (s) => /^\d{4}-\d{2}-\d{2}$/.test(String(s || ''));
export const partesISO = (iso) => {
  const [y, m, d] = String(iso).split('-').map(Number);
  return { anio: y, mes: m, dia: d };
};

// ISO solo si la fecha existe (31/02 no; 29/02/2028 sí) y el año es razonable
export const armarISO = (anio, mes1, dia) => {
  if (!(anio >= 1900 && anio <= 2200)) return null;
  if (!(mes1 >= 1 && mes1 <= 12)) return null;
  if (!(dia >= 1 && dia <= diasEnMes(anio, mes1))) return null;
  return `${anio}-${pad2(mes1)}-${pad2(dia)}`;
};

export const formatearFecha = (iso) => {
  if (!esISO(iso)) return '';
  const { anio, mes, dia } = partesISO(iso);
  return `${pad2(dia)}/${pad2(mes)}/${anio}`;
};

// Máscara que respeta cómo teclea la gente: "10092026" se parte solo en
// 10/09/2026, "1/9/2026" se acepta con un dígito, y punto, guion o espacio
// valen como barra. Día y mes admiten 2 dígitos, el año 4; lo que sobra se tira.
export const mascaraFecha = (texto) => {
  const limpio = String(texto || '').replace(/[.\-\s]/g, '/').replace(/[^\d/]/g, '');
  const grupos = ['', '', ''];
  let g = 0;
  for (const ch of limpio) {
    if (ch === '/') { if (grupos[g].length > 0 && g < 2) g++; continue; }
    const max = g === 2 ? 4 : 2;
    if (grupos[g].length >= max) { if (g < 2) g++; else continue; }
    grupos[g] += ch;
  }
  let out = grupos[0];
  if (g >= 1) out += '/' + grupos[1];
  if (g >= 2) out += '/' + grupos[2];
  return out;
};

// Lee lo tecleado o pegado: dd/mm/aaaa, d/m/aa, ddmmaaaa o aaaa-mm-dd.
// null si no es una fecha real.
export const parsearFecha = (texto) => {
  const t = String(texto || '').trim();
  if (!t) return null;
  let m = t.match(/^(\d{4})-(\d{1,2})-(\d{1,2})$/);
  if (m) return armarISO(+m[1], +m[2], +m[3]);
  m = t.match(/^(\d{1,2})[/.-](\d{1,2})[/.-](\d{2}|\d{4})$/);
  if (m) return armarISO(m[3].length === 2 ? 2000 + +m[3] : +m[3], +m[2], +m[1]);
  m = t.match(/^(\d{2})(\d{2})(\d{4})$/);
  if (m) return armarISO(+m[3], +m[2], +m[1]);
  return null;
};

// ¿El texto ya trae el año completo? Mientras se teclea solo se emite entonces:
// "10/09/20" no debe convertirse en 2020 a medio camino.
export const textoCompleto = (texto) => /\d{4}$/.test(String(texto || '').trim());

export const sumarDias = (iso, n) => addDias(iso, n);

// 42 celdas (6 semanas) del mes, de lunes a domingo; `delMes` distingue el relleno
export const celdasMes = (anio, mes1) => {
  const primero = `${anio}-${pad2(mes1)}-01`;
  const desdeLunes = (new Date(Date.UTC(anio, mes1 - 1, 1)).getUTCDay() + 6) % 7;
  const inicio = addDias(primero, -desdeLunes);
  const out = [];
  for (let i = 0; i < 42; i++) {
    const iso = addDias(inicio, i);
    const p = partesISO(iso);
    out.push({ iso, dia: p.dia, delMes: p.mes === mes1 && p.anio === anio });
  }
  return out;
};

export const mesRelativo = (anio, mes1, delta) => {
  const t = anio * 12 + (mes1 - 1) + delta;
  return { anio: Math.floor(t / 12), mes: (t % 12) + 1 };
};
